import { FC, useContext, useEffect, useRef, useState } from "react";
import clsx from "clsx";

import ThemeContext from "~/contexts/ThemeContext";
import { MenuTab } from "~/entities/menuEntities";
import {
  MenuTabBorderRadius,
  MenuTabFontSize,
  MenuTabImageBorderRadius,
} from "~/theme/models/themeModels";
import { TabsPosition } from "~/entities/templateEntities";
import PlaceholderImage from "~/images/PlaceholderImage.png";

import styles from "./MenuTabs.module.css";
import { uploadcareConfig } from "~/configs/uploadcareConfig";

type MenuTabsProps = {
  menuTabsRef: React.RefObject<HTMLDivElement>;
  tabs: MenuTab[];
  selectedTabCode: string | null;
  scrollToSection: (code: string) => void;
};

const MenuTabs: FC<MenuTabsProps> = ({
  menuTabsRef,
  tabs,
  selectedTabCode,
  scrollToSection,
}) => {
  const [hoveredTab, setHoveredTab] = useState<MenuTab | null>(null);

  const { template } = useContext(ThemeContext);

  const tabRefs = useRef<Record<string, HTMLDivElement | null>>({});

  /**
   * Scroll to selected tab
   */
  useEffect(() => {
    if (!selectedTabCode) return;

    const el = tabRefs.current[selectedTabCode];
    const parent = el?.parentElement as HTMLElement | null;
    if (!el || !parent) return;

    const parentRect = parent.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    const elements = Object.values(tabRefs.current).filter(
      Boolean,
    ) as HTMLElement[];
    const firstEl = elements[0];
    const lastEl = elements[elements.length - 1];

    if (el === firstEl) {
      parent.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }

    if (el === lastEl) {
      parent.scrollTo({
        left: parent.scrollWidth - parent.clientWidth,
        behavior: "smooth",
      });
      return;
    }

    const fullyVisible =
      elRect.left >= parentRect.left && elRect.right <= parentRect.right;
    if (fullyVisible) return;

    let targetScroll = parent.scrollLeft;

    if (elRect.right > parentRect.right) {
      const diff = elRect.right - parentRect.right + 4;
      targetScroll = parent.scrollLeft + diff;
    } else if (elRect.left < parentRect.left) {
      const diff = parentRect.left - elRect.left + 4;
      targetScroll = parent.scrollLeft - diff;
    }

    parent.scrollTo({ left: targetScroll, behavior: "smooth" });
  }, [selectedTabCode]);

  // Styles
  // Global
  const menuItemFontColour = template.menuScreen.tabs.itemFontColour;
  const menuItemBackgroundColour =
    template.menuScreen.tabs.itemBackgroundColour;
  const menuItemBorderColour = template.menuScreen.tabs.itemBorderColour;
  const selectedMenuItemBackgroundColour =
    template.menuScreen.tabs.selectedItemBackgroundColour;
  const selectedMenuItemFontColour =
    template.menuScreen.tabs.selectedItemFontColour;
  const selectedMenuItemBorderColour =
    template.menuScreen.tabs.selectedItemBorderColour;
  const menuItemFontSize = MenuTabFontSize[template.menuScreen.tabs.fontSize];
  // Width image
  const withImage = template.menuScreen.tabs.widthImage;
  const menuItemImageBorderRadius =
    MenuTabImageBorderRadius[template.menuScreen.tabs.imageShape];
  // Base
  const menuBackgroundColor = template.menuScreen.tabs.backgroundColour;
  const menuItemBorderRadius =
    MenuTabBorderRadius[template.menuScreen.tabs.itemType];
  const menuBorderColor = template.menuScreen.tabs.borderColour;
  const shadow = template.menuScreen.tabs.shadow;
  // Position
  const menuTabsBottom =
    template.menuScreen.tabs.position === TabsPosition.Bottom;

  return (
    <div
      ref={menuTabsRef}
      data-sticky="menu-tabs"
      className={clsx(
        styles.wrapper,
        withImage && styles.wrapperWithImage,
        menuTabsBottom && styles.bottom,
      )}
      style={{
        backgroundColor: menuBackgroundColor,
        boxShadow: shadow ? "10px 4px 10px 0px rgba(86, 86, 86, 0.07)" : "none",
        borderColor: menuBorderColor,
      }}
    >
      <div
        className={clsx(
          styles.menuWrapper,
          withImage && styles.menuWrapperWithImage,
        )}
        style={{
          padding: withImage ? "16px" : "10px 11px",
        }}
      >
        {!withImage &&
          tabs
            .filter((tab) => tab.items && tab.items.length > 0)
            .map((item) => (
              <div
                ref={(el) => (tabRefs.current[item.code] = el)}
                key={item.code}
                className={styles.menuItem}
                style={{
                  backgroundColor:
                    item.code === selectedTabCode
                      ? selectedMenuItemBackgroundColour
                      : menuItemBackgroundColour,
                  color:
                    item.code === selectedTabCode
                      ? selectedMenuItemFontColour
                      : menuItemFontColour,
                  fontSize: menuItemFontSize,
                  borderRadius: menuItemBorderRadius,
                  borderColor:
                    item.code === selectedTabCode
                      ? selectedMenuItemBorderColour
                      : menuItemBorderColour,
                }}
                onClick={() => {
                  scrollToSection(item.code);
                }}
              >
                {item.name}
              </div>
            ))}
        {withImage &&
          tabs
            .filter((tab) => tab.items && tab.items.length > 0)
            .map((tab) => (
              <div
                ref={(el) => (tabRefs.current[tab.code] = el)}
                key={tab.code}
                className={styles.menuItemWithImage}
                style={{
                  color: menuItemFontColour,
                  fontSize: menuItemFontSize,
                  fontWeight: tab.code === selectedTabCode ? 600 : 400,
                }}
                onClick={() => {
                  scrollToSection(tab.code);
                }}
              >
                <div
                  className={styles.imageWrapper}
                  style={{
                    borderColor:
                      tab.code === selectedTabCode
                        ? selectedMenuItemBackgroundColour
                        : "transparent",
                    borderRadius: menuItemImageBorderRadius,
                  }}
                  onFocus={() => {}}
                  onMouseOver={() => setHoveredTab(tab)}
                  onMouseLeave={() => setHoveredTab(null)}
                >
                  {tab.code === selectedTabCode ||
                  tab.code === hoveredTab?.code ? (
                    <img
                      src={
                        tab.hoverImage
                          ? uploadcareConfig.getUrl(
                              tab.hoverImage.fileId,
                              tab.hoverImage.modifiers,
                            )
                          : tab.image
                            ? uploadcareConfig.getUrl(
                                tab.image.fileId,
                                tab.image.modifiers,
                              )
                            : PlaceholderImage
                      }
                      alt={
                        tab.hoverImage?.fileName ??
                        tab.image?.fileName ??
                        "PlaceholderImage"
                      }
                    />
                  ) : (
                    <img
                      src={
                        tab.image
                          ? uploadcareConfig.getUrl(
                              tab.image.fileId,
                              tab.image.modifiers,
                            )
                          : PlaceholderImage
                      }
                      alt={tab.image?.fileName ?? "PlaceholderImage"}
                    />
                  )}
                </div>
                {tab.name}
              </div>
            ))}
      </div>
    </div>
  );
};

export default MenuTabs;
